import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  PaperProps,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import Draggable from "react-draggable";

export type DialogType =
  | "success"
  | "alter"
  | "alert"
  | "error"
  | "confirm"
  | "confirmCancel"
  | "info"
  | "warning";

interface DialogProps {
  type: DialogType;
  message?: string;
  onResolve: (value: boolean | void) => void;
}

function PaperComponent(props: PaperProps) {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
      handle=".draggable-dialog"
      cancel={'[class*="MuiDialogContent-root"]'}
      bounds="body"
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

/** 종류별 제목 · 아이콘 */
function dialogHead(type: DialogType): { title: string; icon: React.ReactNode } {
  switch (type) {
    case "success":
      return { title: "성공", icon: <CheckCircleIcon color="success" style={{ marginRight: 8 }} /> };
    case "alert":
    case "alter":
      return { title: "경고", icon: <WarningIcon color="warning" style={{ marginRight: 8 }} /> };
    case "error":
      return { title: "오류", icon: <ErrorIcon color="error" style={{ marginRight: 8 }} /> };
    case "info":
      return { title: "알림", icon: <InfoIcon color="info" style={{ marginRight: 8 }} /> };
    case "confirm":
    case "confirmCancel":
      return { title: "확인", icon: <InfoIcon color="info" style={{ marginRight: 8 }} /> };
    default:
      return { title: "", icon: null };
  }
}

const DialogHelper = ({ type, message = "", onResolve }: DialogProps) => {
  const [open, setOpen] = useState(true);
  /* 제목 · 아이콘은 첫 그림부터 같이 그린다. useEffect 로 나중에 채우면 첫 순간 문구만 보였다가
     제목이 붙어 안내창이 두 번 뜬 것처럼 보였다 (2026-10-02) */
  const { title, icon } = dialogHead(type);

  useEffect(() => {
    const handleEnterKey = (e: KeyboardEvent) => {
      if (
        open &&
        e.key === "Enter" &&
        type !== "confirm" &&
        type !== "confirmCancel"
      ) {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEnterKey);
    return () => {
      document.removeEventListener("keydown", handleEnterKey);
    };
  }, [open, type]);

  const handleClose = (result?: boolean) => {
    setOpen(false);
    onResolve(result);
  };

  return (
    <Dialog
      open={open}
      onClose={(e: any, reason) => {
        if (reason === "escapeKeyDown") {
          e.preventDefault();
          handleClose(false);
        }
      }}
      disableRestoreFocus
      PaperComponent={PaperComponent}
      slotProps={{
        paper: {
          sx: {
            margin: 0,
          },
        },
      }}
    >
      <DialogTitle style={{ cursor: "move" }} className="draggable-dialog">
        <Box display="flex" alignItems="center">
          {icon}
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {message.split("\n").map((line, index) => (
            <React.Fragment key={index}>
              {line}
              <br />
            </React.Fragment>
          ))}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 1, // 버튼 간격
          minWidth: 300,
        }}
      >
        {type === "confirm" ? (
          <>
            <Button
              sx={{ width: 80 }}
              onClick={() => handleClose(true)}
              variant="contained"
            >
              예
            </Button>
            <Button
              sx={{ width: 80 }}
              onClick={() => handleClose(false)}
              variant="outlined"
            >
              아니오
            </Button>
          </>
        ) : type === "confirmCancel" ? (
          <>
            <Button
              sx={{ width: 80 }}
              onClick={() => handleClose(true)}
              variant="contained"
            >
              예
            </Button>
            <Button
              sx={{ width: 80 }}
              onClick={() => handleClose(false)}
              variant="outlined"
            >
              아니오
            </Button>
            <Button
              sx={{ width: 80 }}
              onClick={() => handleClose(undefined)}
              variant="outlined"
            >
              취소
            </Button>
          </>
        ) : (
          <Button
            sx={{ width: 80 }}
            onClick={() => handleClose()}
            variant="outlined"
          >
            확인
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Dialog를 띄우는 함수
export const showDialog = (
  type: DialogType,
  message: string
): Promise<boolean | void> => {
  const container = document.createElement("div");
  document.body.appendChild(container);

  const root = ReactDOM.createRoot(container);

  return new Promise<boolean | void>((resolve) => {
    const handleResolve = (value: boolean | void) => {
      root.unmount();
      document.body.removeChild(container);
      resolve(value);
    };

    root.render(
      <DialogHelper type={type} message={message} onResolve={handleResolve} />
    );
  });
};
